import { useSelector } from 'react-redux'
import { useCallback, useMemo } from 'react'
import { dispatch } from 'store'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import { translate } from 'utils/translations'
import { replaceTranslationVariables } from 'utils/text-transformation'
import { T } from 'consts/translationKeys'
import { PROJECT_MEDIA_TYPES } from 'consts'
import { MediaStatus } from 'types/media'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import {
  isVideoConsistencyFeatureAvailableSelector,
  isSmartEditModeSelector
} from 'store/selectors'
import { creditsSelectors } from 'store/features/credits/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { isPremiumModel, handlePremiumModelClick } from 'utils'
import { IResolvedVisualMediaPlacement } from 'utils/visualMediaPlacement'
import { useVisualMediaPlacement } from './timeline/useVisualMediaPlacement'
import { REF_LINKS } from 'consts/refLinks'
import { useOpenUpgradePopup } from 'hooks/useOpenUpgradePopup'
import { useTimelineContext } from 'context/TimelineContext'

interface IUseCreditsCheckResult {
  hasInsufficientCredits: boolean
  handleInsufficientCredits: () => void
}

export const useCreditsCheck = (
  videoGenerationCost: number
): IUseCreditsCheckResult => {
  const userCredits = useSelector(creditsSelectors.userCredits) || 0
  const hasInsufficientCredits = userCredits < videoGenerationCost
  const openUpgradePopup = useOpenUpgradePopup({ usePlanCheck: true })

  const handleInsufficientCredits = () => {
    openUpgradePopup({ ref: REF_LINKS.V_EDITOR_AI_VIDEO_GENERATE })
  }

  return {
    hasInsufficientCredits,
    handleInsufficientCredits
  }
}

const generateSmartEditedAIVideo = () =>
  dispatch(aiVideoThunks.generateSmartEditedAIVideo())

const generateConsistentAIVideo = (placement: IResolvedVisualMediaPlacement) =>
  dispatch(aiVideoThunks.generateConsistentAIVideo(placement))

const regenerateAIVideo = () => dispatch(aiVideoThunks.regenerateAiVideo())

const generateAIVideo = (placement?: IResolvedVisualMediaPlacement) =>
  dispatch(aiVideoThunks.generateAiVideo(placement))

interface IUseGenerateActionResult {
  handleGenerate: () => void
  generateButtonLabel: string
  tooltipText: string
  hasInsufficientCredits: boolean
  generationCredits: number
  isApproximateCost: boolean
  imageResizeCost: number
}

interface IUseGenerationCreditsResult {
  generationCredits: number
  generationCreditsIfPowerOn: number
  isApproximateCost: boolean
  imageResizeCost: number
}

export const useGenerationCredits = (): IUseGenerationCreditsResult => {
  const videoGenerationCost = useSelector(aiVideoSelectors.videoGenerationCost)
  const videoGenerationCostIfPowerOn = useSelector(
    aiVideoSelectors.videoGenerationCostIfPowerOn
  )
  const isConsistencyFeatureAvailable = useSelector(
    isVideoConsistencyFeatureAvailableSelector
  )
  const isConsistencyEnabled = useSelector(
    aiVideoSelectors.isConsistencyEnabled
  )
  const isSmartMode = useSelector(isSmartEditModeSelector)
  const defaultImageModelCost = useSelector(aiImageSelectors.defaultModelCost)
  const imageProcessingCosts = useSelector(
    aiVideoSelectors.imageProcessingCosts
  )

  const isSmartGeneration =
    (isConsistencyFeatureAvailable && isConsistencyEnabled) || isSmartMode
  const imageProcessingCost =
    imageProcessingCosts.restyleCost + imageProcessingCosts.resizeCost
  const imageAddOnCost = isSmartGeneration
    ? defaultImageModelCost
    : imageProcessingCost

  return {
    generationCredits: imageAddOnCost + videoGenerationCost,
    generationCreditsIfPowerOn: imageAddOnCost + videoGenerationCostIfPowerOn,
    isApproximateCost: isSmartGeneration,
    imageResizeCost: imageProcessingCosts.resizeCost
  }
}

export const useGenerateAction = (): IUseGenerateActionResult => {
  const targetMediaItemId = useSelector(aiVideoSelectors.targetMediaItemId)
  const mediaItems = useSelector(projectDataSelectors.mediaItems)
  const duration = useSelector(aiVideoSelectors.duration)
  const isConsistencyFeatureAvailable = useSelector(
    isVideoConsistencyFeatureAvailableSelector
  )
  const isConsistencyEnabled = useSelector(
    aiVideoSelectors.isConsistencyEnabled
  )
  const isSmartMode = useSelector(isSmartEditModeSelector)
  const isConsistentGeneration =
    isConsistencyFeatureAvailable && isConsistencyEnabled

  const selectedModel = useSelector(aiVideoSelectors.selectedModel)
  const userCurrentPlanId = useSelector(userSelectors.userCurrentPlanId)
  const isFirstAndLastFrameSupported = useSelector(
    aiVideoSelectors.isFirstAndLastFrameSupported
  )

  const getPlacement = useVisualMediaPlacement()

  const { generationCredits, isApproximateCost, imageResizeCost } =
    useGenerationCredits()
  const imageProcessingCosts = useSelector(
    aiVideoSelectors.imageProcessingCosts
  )

  const { setPlayHeadCurrentTime, isPlaying } = useTimelineContext()

  const targetMediaItem = useMemo(
    () => mediaItems.find(item => item.id === String(targetMediaItemId)),
    [mediaItems, targetMediaItemId]
  )

  const isRegenerating =
    targetMediaItem?.type === PROJECT_MEDIA_TYPES.aiVideo &&
    !!targetMediaItemId &&
    targetMediaItem?.meta?.status !== MediaStatus.PENDING

  const isImageAnimation =
    !!targetMediaItemId &&
    !!targetMediaItem &&
    targetMediaItem.type !== PROJECT_MEDIA_TYPES.aiVideo

  const tooltipText = !selectedModel
    ? translate(T.SELECT_MODEL_TO_GENERATE_TOOLTIP)
    : isSmartMode
      ? translate(T.ENTER_PROMPT_TO_GENERATE)
      : imageResizeCost > 0
        ? replaceTranslationVariables(
            translate(
              imageProcessingCosts.resizeFrameCount > 1
                ? T.REFERENCE_IMAGES_RESIZE_CREDITS
                : T.REFERENCE_IMAGE_RESIZE_CREDITS
            ),
            [String(imageResizeCost)]
          )
        : translate(
            isFirstAndLastFrameSupported
              ? T.ADD_PROMPT_OR_START_FRAME_TO_GENERATE
              : T.ADD_PROMPT_OR_REFERENCE_IMAGE_TO_GENERATE
          )

  const generateButtonLabel = isRegenerating
    ? translate(T.RE_GENERATE)
    : translate(T.GENERATE_LABEL)

  const { hasInsufficientCredits, handleInsufficientCredits } =
    useCreditsCheck(generationCredits)

  const { isDurationAllowed } = useDurationLimitCheck()

  const isSelectedModelPremium = selectedModel
    ? isPremiumModel(selectedModel, userCurrentPlanId)
    : false

  const handlePremiumModelCheck = useCallback(() => {
    handlePremiumModelClick(selectedModel, userCurrentPlanId)
  }, [selectedModel, userCurrentPlanId])

  const withDurationCheck = useCallback(
    (action: () => void) => {
      return isDurationAllowed(duration) ? action() : void 0
    },
    [isDurationAllowed, duration]
  )

  const handleGenerate = useCallback(() => {
    const actionsPerCondition = [
      {
        condition: isSelectedModelPremium,
        action: () => withDurationCheck(handlePremiumModelCheck)
      },
      {
        condition: hasInsufficientCredits,
        action: () => withDurationCheck(handleInsufficientCredits)
      },
      {
        condition: isSmartMode,
        action: () =>
          withDurationCheck(() => {
            generateSmartEditedAIVideo()
            if (targetMediaItem) {
              setPlayHeadCurrentTime(targetMediaItem.startTime, isPlaying)
            }
          })
      },
      {
        condition: isConsistentGeneration,
        action: () =>
          withDurationCheck(() => {
            const placement = getPlacement(duration)
            generateConsistentAIVideo(placement)
            setPlayHeadCurrentTime(placement.startTime, isPlaying)
          })
      },
      {
        condition: isRegenerating,
        action: () =>
          withDurationCheck(() => {
            regenerateAIVideo()
            if (targetMediaItem) {
              setPlayHeadCurrentTime(targetMediaItem.startTime, isPlaying)
            }
          })
      },
      {
        condition: isImageAnimation,
        action: () =>
          withDurationCheck(() => {
            generateAIVideo()
            if (targetMediaItem) {
              setPlayHeadCurrentTime(targetMediaItem.startTime, isPlaying)
            }
          })
      },
      {
        condition: true,
        action: () =>
          withDurationCheck(() => {
            const placement = getPlacement(duration)
            generateAIVideo(placement)
            setPlayHeadCurrentTime(placement.startTime, isPlaying)
          })
      }
    ]

    actionsPerCondition.find(action => action.condition)!.action()
  }, [
    isSelectedModelPremium,
    handlePremiumModelCheck,
    withDurationCheck,
    hasInsufficientCredits,
    handleInsufficientCredits,
    isSmartMode,
    isConsistentGeneration,
    isRegenerating,
    isImageAnimation,
    getPlacement,
    isPlaying,
    setPlayHeadCurrentTime,
    duration,
    targetMediaItem
  ])

  return {
    hasInsufficientCredits,
    handleGenerate,
    generateButtonLabel,
    tooltipText,
    generationCredits,
    isApproximateCost,
    imageResizeCost
  }
}
